import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { EmptyState, StatusPill, type StatusTone } from '../../../components/app';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  cashflowApi,
  type StatementEntryStatus,
  type StatementPreview,
} from '../../../api/cashflow';
import { StatementImportDialogProps } from '../types';
import { getErrorMessage } from '@/lib/errors';
import { formatNumber } from '../format';

/**
 * Импорт выписки банка (ADR 0026): файл → предпросмотр со статусом каждой строки
 * → подтверждение. До подтверждения в базу ничего не пишется; повторный импорт
 * того же или пересекающегося файла дублей не создаёт.
 */

const STATUS_META: Record<StatementEntryStatus, { label: string; hint: string; summaryHint: string; tone: StatusTone; text: string }> = {
  new: { label: 'Новое', hint: 'станет Поступлением', summaryHint: 'станут Поступлениями', tone: 'pos', text: 'text-pos' },
  matched_manual: {
    label: 'Уже заведено вручную',
    hint: 'привяжется к ручной записи',
    summaryHint: 'привяжутся, без дубля',
    tone: 'neutral',
    text: 'text-text',
  },
  imported: { label: 'Уже импортировано', hint: 'пропускаем', summaryHint: 'пропускаем', tone: 'neutral', text: 'text-text-2' },
  foreign: {
    label: 'Не от провайдера',
    hint: 'не импортируется',
    summaryHint: 'другая деятельность, P2P',
    tone: 'warn',
    text: 'text-warn',
  },
  debit: { label: 'Списание', hint: 'не импортируется', summaryHint: 'не импортируется', tone: 'neg', text: 'text-neg' },
};

const ORDER: StatementEntryStatus[] = ['new', 'matched_manual', 'imported', 'foreign', 'debit'];

export function StatementImportDialog({
  isOpen,
  onOpenChange,
  onImported,
  formatMoney,
}: StatementImportDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<StatementPreview | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);

  useEffect(() => {
    if (isOpen) return;
    setFile(null);
    setPreview(null);
    setShowSkipped(false);
  }, [isOpen]);

  const handleFile = async (picked: File | undefined) => {
    if (!picked) return;
    setFile(picked);
    setPreview(null);
    setIsParsing(true);
    try {
      setPreview(await cashflowApi.previewStatement(picked));
    } catch (error: any) {
      setFile(null);
      toast.error(getErrorMessage(error, 'Не удалось прочитать выписку'));
    } finally {
      setIsParsing(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const toCommit = (preview?.summary.new.count ?? 0) + (preview?.summary.matched_manual.count ?? 0);

  const handleImport = async () => {
    if (!file || isImporting) return;
    setIsImporting(true);
    try {
      const result = await cashflowApi.importStatement(file);
      const parts = [
        result.created && `создано ${result.created}`,
        result.linked && `привязано к ручным ${result.linked}`,
      ].filter(Boolean);
      toast.success(parts.length ? `Импорт выписки: ${parts.join(', ')}` : 'Новых поступлений нет');
      onImported();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Не удалось импортировать выписку'));
    } finally {
      setIsImporting(false);
    }
  };

  const visibleEntries = useMemo(() => {
    if (!preview) return [];
    const relevant = preview.entries.filter((e) => showSkipped || (e.status !== 'foreign' && e.status !== 'debit'));
    return [...relevant].sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || a.date.localeCompare(b.date));
  }, [preview, showSkipped]);

  const skippedCount = (preview?.summary.foreign.count ?? 0) + (preview?.summary.debit.count ?? 0);
  const newCount = preview?.summary.new.count ?? 0;
  const linkCount = preview?.summary.matched_manual.count ?? 0;
  const commitLabel =
    preview && toCommit === 0
      ? 'Нечего импортировать'
      : `Импортировать ${newCount} ${newCount === 1 ? 'новое' : 'новых'}` + (linkCount ? ` и привязать ${linkCount}` : '');

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !isImporting && onOpenChange(open)}>
      <DialogContent className="max-w-5xl gap-0 p-0">
        <DialogHeader bordered>
          <DialogTitle>{preview ? 'Импорт выписки · предпросмотр' : 'Импорт выписки'}</DialogTitle>
          <DialogDescription>
            {preview && file
              ? `${file.name} · ${preview.entries.length} ${plural(preview.entries.length, 'проводка', 'проводки', 'проводок')} · повторный импорт ничего не задублирует`
              : 'Выписка Ипотека-банка в .xlsx. В Поступления попадают только зачисления от провайдеров; уже импортированные Проводки и записи, заведённые вручную, повторно не создаются.'}
          </DialogDescription>
        </DialogHeader>

        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />

        <DialogBody>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={isParsing || isImporting}
            className="flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-border p-4 text-left outline-none transition-colors hover:border-accent-text focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          >
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-muted text-text-2 [&_svg]:size-5">
              {isParsing ? <Loader2 className="animate-spin" /> : file ? <FileSpreadsheet /> : <Upload />}
            </span>
            <span className="flex flex-col">
              <span className="text-sm font-semibold">{file ? file.name : 'Выбрать файл выписки'}</span>
              <span className="text-xs text-text-2">
                {isParsing ? 'Читаем выписку…' : file ? 'Нажмите, чтобы выбрать другой файл' : '.xlsx, до 5 МБ'}
              </span>
            </span>
          </button>

          {preview &&
            preview.warnings.map((w) => (
              <div key={w} role="status" className="flex gap-2 rounded-xl bg-warn-soft p-3 text-sm text-warn">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>{w}</span>
              </div>
            ))}

          {preview && (
            <ul aria-label="Итог по статусам" className="m-0 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-3 lg:grid-cols-5">
              {ORDER.map((status) => {
                const row = preview.summary[status];
                const meta = STATUS_META[status];
                return (
                  <li key={status} className="flex flex-col gap-0.5 rounded-xl border border-border-soft bg-surface-alt px-3 py-2.5">
                    <span className={cn('flex items-center gap-1.5 text-xs font-bold', meta.text)}>
                      <span aria-hidden className="size-2 rounded-full bg-current" />
                      {meta.label}
                    </span>
                    <b className="font-display text-lg tabular-nums">{row.count}</b>
                    <span className="text-xs text-text-2 tabular-nums">{formatMoney(row.sum)}</span>
                    <span className="text-xs text-text-2">{meta.summaryHint}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogBody>

        {preview && (
          <Table className="min-w-[860px]" containerClassName="max-h-[45vh] overflow-y-auto">
            <TableHeader>
              <TableRow>
                <TableHead>Дата</TableHead>
                <TableHead>Счёт отправителя</TableHead>
                <TableHead>Назначение платежа</TableHead>
                <TableHead className="text-right">Сумма, сум</TableHead>
                <TableHead>Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleEntries.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={5}>
                    <EmptyState title="Зачислений от провайдеров в файле нет" />
                  </TableCell>
                </TableRow>
              ) : (
                visibleEntries.map((e) => {
                  const meta = STATUS_META[e.status];
                  return (
                    <TableRow key={`${e.fingerprint}-${e.status}`}>
                      <TableCell className="whitespace-nowrap py-2.5 text-text-2 tabular-nums">
                        {e.date} {e.time}
                        <span className="block text-xs">№{e.document}</span>
                      </TableCell>
                      <TableCell className="py-2.5">
                        <span className="flex flex-col gap-0.5">
                          <b>{e.providerName ?? (e.senderName || 'Без провайдера')}</b>
                          <span className="font-mono text-xs text-text-2">{e.senderAccount ?? '—'}</span>
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[320px] py-2.5 text-text-2">
                        <span className="line-clamp-2" title={e.purpose}>
                          {e.purpose || '—'}
                        </span>
                      </TableCell>
                      <TableCell
                        className={cn('whitespace-nowrap py-2.5 text-right font-semibold tabular-nums', e.status === 'debit' && 'text-neg')}
                      >
                        {formatNumber(e.status === 'debit' ? -e.debit : e.credit)}
                      </TableCell>
                      <TableCell className="py-2.5">
                        <span className="flex flex-col items-start gap-1">
                          <StatusPill size="sm" tone={meta.tone} className={e.status === 'imported' ? 'text-text-2' : undefined}>
                            {meta.label}
                          </StatusPill>
                          <span className="text-xs text-text-2">{meta.hint}</span>
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}

        <DialogFooter bordered className="sm:justify-between">
          {preview ? (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Checkbox id="cf-show-skipped" checked={showSkipped} onCheckedChange={(v) => setShowSkipped(v === true)} />
                <Label htmlFor="cf-show-skipped" className="font-normal text-text-2">
                  Показать пропущенные строки ({skippedCount})
                </Label>
              </div>
              <span className="text-xs text-text-2">
                Показано {visibleEntries.length} из {preview.entries.length}
              </span>
            </div>
          ) : (
            <span />
          )}
          <div className="flex flex-col-reverse gap-2.5 sm:flex-row">
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isImporting}>
              Отмена
            </Button>
            <Button variant="inverse" onClick={handleImport} disabled={!preview || toCommit === 0 || isImporting}>
              {isImporting && <Loader2 className="animate-spin" />}
              {commitLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function plural(n: number, one: string, few: string, many: string) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}
